// @catalog F.048
// Resultados de la ingesta Walmart: líneas del export "PO Data" (una fila por
// paquete, como el export original), con filtro por mes y paginación. Sin
// economía del canal: el export no trae comisiones.
import { useState } from "react";
import {
  Box,
  Chip,
  Pagination,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
  BodyCell,
  BodyRow,
  HeaderCell,
  TableShell,
} from "../analytics/TableCells";
import MercadoLibreImportService, {
  type MlOperationStatus,
} from "../../services/mercadoLibreImport.service";

const fmt = MercadoLibreImportService.formatCurrency;
const PAGE_SIZE = 50;

const STATUS_LABEL: Record<MlOperationStatus, string> = {
  preparacion: "Preparación",
  en_camino: "En camino",
  entregado: "Entregado",
  devuelto_reembolsado: "Devuelto/Reembolsado",
  cancelado: "Cancelado",
  desconocido: "Desconocido",
};

const STATUS_COLOR: Record<
  MlOperationStatus,
  "default" | "info" | "success" | "warning" | "error"
> = {
  preparacion: "info",
  en_camino: "warning",
  entregado: "success",
  devuelto_reembolsado: "error",
  cancelado: "default",
  desconocido: "default",
};

export function WalmartPedidosBlock(): JSX.Element {
  const [month, setMonth] = useState("");
  const [page, setPage] = useState(1);

  const linesQuery = useQuery({
    queryKey: ["walmart-lines", month, page],
    queryFn: () =>
      MercadoLibreImportService.getWalmartLines({
        month: month || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: month === "" || /^\d{4}-\d{2}$/.test(month),
  });
  const data = linesQuery.data;
  const pageCount = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        useFlexGap
        sx={{ mb: 1, flexWrap: "wrap", rowGap: 1 }}
      >
        <Typography
          component="h2"
          sx={{ fontSize: "16px", fontWeight: 600, color: "var(--ink-1)" }}
        >
          Líneas de pedido Walmart
        </Typography>
        <Box sx={{ flex: 1 }} />
        {data && (
          <Typography sx={{ fontSize: "12.5px", color: "var(--ink-3)" }}>
            {data.total.toLocaleString()} líneas (una por paquete)
          </Typography>
        )}
        <TextField
          size="small"
          type="month"
          label="Mes (todos si vacío)"
          value={month}
          onChange={(e) => {
            setMonth(e.target.value);
            setPage(1);
          }}
          sx={{ width: 190 }}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>

      <TableShell maxHeight="max(360px, calc(100vh - 295px))">
        <Box component="thead">
          <Box component="tr">
            <HeaderCell>Fecha</HeaderCell>
            <HeaderCell>Orden</HeaderCell>
            <HeaderCell>Estado</HeaderCell>
            <HeaderCell>SKU</HeaderCell>
            <HeaderCell>Producto</HeaderCell>
            <HeaderCell align="right">Cant.</HeaderCell>
            <HeaderCell align="right">Precio unit.</HeaderCell>
            <HeaderCell align="right">Envío</HeaderCell>
            <HeaderCell>Paquetería</HeaderCell>
          </Box>
        </Box>
        <Box component="tbody">
          {(data?.rows ?? []).length === 0 && (
            <Box component="tr">
              <BodyCell muted>
                {linesQuery.isLoading
                  ? "Cargando…"
                  : "Sin líneas para el filtro."}
              </BodyCell>
            </Box>
          )}
          {(data?.rows ?? []).map((r, i) => (
            <BodyRow key={`${r.poId}|${r.lineNumber}|${i}`}>
              <BodyCell muted>{r.orderDate}</BodyCell>
              <BodyCell>
                <Tooltip
                  arrow
                  title={`PO ${r.poId} · línea ${r.lineNumber}${r.deliveryDate ? ` · entregado ${r.deliveryDate}` : ""}`}
                >
                  <span>{r.orderNumber}</span>
                </Tooltip>
              </BodyCell>
              <BodyCell>
                <Tooltip arrow title={r.statusRaw ?? ""}>
                  <Chip
                    size="small"
                    label={STATUS_LABEL[r.status]}
                    color={STATUS_COLOR[r.status]}
                    variant="outlined"
                  />
                </Tooltip>
              </BodyCell>
              <BodyCell muted>{r.sku ?? "—"}</BodyCell>
              <BodyCell>
                <Tooltip arrow title={r.productName ?? ""}>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      maxWidth: 260,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      verticalAlign: "bottom",
                    }}
                  >
                    {r.productName ?? "—"}
                  </Box>
                </Tooltip>
              </BodyCell>
              <BodyCell align="right">{r.quantity ?? "—"}</BodyCell>
              <BodyCell align="right">{fmt(r.unitPrice)}</BodyCell>
              <BodyCell align="right">{fmt(r.shippingCost)}</BodyCell>
              <BodyCell muted>
                {r.trackingNumber ? (
                  <Tooltip arrow title={r.trackingNumber}>
                    <span>{r.carrier ?? "—"}</span>
                  </Tooltip>
                ) : (
                  (r.carrier ?? "—")
                )}
              </BodyCell>
            </BodyRow>
          ))}
        </Box>
      </TableShell>
      {pageCount > 1 && (
        <Stack alignItems="center" sx={{ mt: 1.5 }}>
          <Pagination
            size="small"
            count={pageCount}
            page={page}
            onChange={(_e, p) => setPage(p)}
          />
        </Stack>
      )}
    </Box>
  );
}
